// This client was automatically generated by Segment Typewriter. ** Do Not Edit **
// To update this file, run:
//   npx typewriter

/**
 * Triggered when the account creation popup is displayed to the user
 */
export interface AccountCreationPopupClicked {
    /**
     * The number of times the user has seen the popup
     */
    attempt_no: number;
    /**
     * Is true if the page the popup is shown is not linked to a team (Example: Home Page)
     */
    is_team_selectable: boolean;
    /**
     * The page in which the user has viewed the popup
     */
    page_type: AccountCreationPopupClickedPageType;
    /**
     * The version of the popup the user is seeing
     */
    popup_version: string;
    /**
     * Name of related team
     */
    team_id?: string;
    /**
     * Id of related team
     */
    team_name?: string;
    [property: string]: any;
}

/**
 * The page in which the user has viewed the popup
 */
export type AccountCreationPopupClickedPageType = "CATEGORY" | "EVENT" | "HOME";

/**
 * Triggered when the account creation popup is displayed to the user
 */
export interface AccountCreationPopupShown {
    /**
     * The number of times the user has seen the popup
     */
    attempt_no: number;
    /**
     * The page in which the user has viewed the popup
     */
    page_type: AccountCreationPopupClickedPageType;
    /**
     * The version of the popup the user is seeing
     */
    popup_version: string;
    /**
     * Name of related team
     */
    team_id?: string;
    /**
     * Id of related team
     */
    team_name?: string;
    [property: string]: any;
}

/**
 * Triggered when the account creation popup is displayed to the user
 */
export interface AccountCreationPopupSkipped {
    /**
     * The number of times the user has seen the popup
     */
    attempt_no: number;
    /**
     * The page in which the user has viewed the popup
     */
    page_type: AccountCreationPopupClickedPageType;
    /**
     * The version of the popup the user is seeing
     */
    popup_version: string;
    /**
     * Name of related team
     */
    team_id?: string;
    /**
     * Id of related team
     */
    team_name?: string;
    [property: string]: any;
}

/**
 * Triggered when the user clicks on 'add new card' in "payments" section (my profile) or in
 * the product page
 */
export interface AddNewCardClicked {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * Amount of active cards/tokens that the user has (not processing)
     */
    number_active_cards: number;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The source where the event was triggered - 'product_page'/'bidding'/'profile'
     */
    source: AddNewCardClickedSource;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * The channel the user has performed the action on. (Either set to "Web" -
 * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
 *
 * Whether this event was triggered from the website or the mobile app
 */
export type Channel = "mobile" | "web";

/**
 * The source where the event was triggered - 'product_page'/'bidding'/'profile'
 *
 * The location from where the delete action was triggered:
 * 'product_page'/'bidding'/'profile'
 */
export type AddNewCardClickedSource = "bidding" | "product_page" | "profile";

/**
 * The size of the window for web; and of the screen for app.
 */
export type ViewportSize = "desktop" | "mobile" | "tablet";

/**
 * Triggered when a user clicks on an announcement at the top of the website
 */
export interface AnnouncementClicked {
    /**
     * The position of the announcement clicked by the user in all displayed announcements
     */
    announcement_position: number;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The url the user gets redirected to after clicking on the announcement
     */
    target_url: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered when the selects and logs-in via a Google account from the OneTap prompt.
 */
export interface AuthenticatedByOneTap {
    /**
     * The path in which the user has interacted with the One Tap prompt
     */
    source_path?: string;
    [property: string]: any;
}

/**
 * Triggered when a user begins the checkout flow by clicking "pay now" on an order
 */
export interface BeginCheckout {
    /**
     * Categories related to the item. Only one category is provided for each type. For products
     * and auctions, they will be based on the directly connected categories. For categories,
     * they will be based on the other categories that its auctions are linked to (For example
     * the team category Liverpool will have the related sport category "Football" because its
     * events will have the "Football" category linked to them. Similarly, Liverpool may have
     * "Poppy Appeal" as the related other category since one of its events may be linked to it).
     */
    categories?: BeginCheckoutCategories;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * The currency used for the order
     */
    currency: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Items related to an order
     */
    items?: BeginCheckoutItem[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The price of the order
     */
    price: number;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Categories related to the item. Only one category is provided for each type. For products
 * and auctions, they will be based on the directly connected categories. For categories,
 * they will be based on the other categories that its auctions are linked to (For example
 * the team category Liverpool will have the related sport category "Football" because its
 * events will have the "Football" category linked to them. Similarly, Liverpool may have
 * "Poppy Appeal" as the related other category since one of its events may be linked to it).
 */
export interface BeginCheckoutCategories {
    /**
     * The related athlete category
     */
    athlete?: PurpleAthlete;
    /**
     * The related country category
     */
    country?: PurpleCountry;
    /**
     * The related league category
     */
    league?: PurpleLeague;
    /**
     * The related other category
     */
    other?: PurpleOther;
    /**
     * The related sport category
     */
    sport?: PurpleSport;
    /**
     * The related team category
     */
    team?: PurpleTeam;
    [property: string]: any;
}

/**
 * The related athlete category
 */
export interface PurpleAthlete {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related country category
 */
export interface PurpleCountry {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related league category
 */
export interface PurpleLeague {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related other category
 */
export interface PurpleOther {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related sport category
 */
export interface PurpleSport {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related team category
 */
export interface PurpleTeam {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

export interface BeginCheckoutItem {
    /**
     * The currency of the item
     */
    currency?: string;
    /**
     * Name of the event related to the product
     */
    event_name?: string;
    /**
     * The framing type of the item
     */
    framing_type?: string;
    /**
     * Index in the list
     */
    index: number;
    /**
     * The id of the item
     */
    item_id: string;
    /**
     * The name of the item
     */
    item_name?: string;
    /**
     * The price of the item related to the currency
     */
    price?: number;
    /**
     * The quantity of items
     */
    quantity: number;
    /**
     * The variant of the item
     */
    variant?: string;
    [property: string]: any;
}

/**
 * Triggered when a user bids on an item. This is triggered both for quick bid and
 * conventional bid.
 */
export interface BidClicked {
    /**
     * The end date of the related auction event.
     */
    auction_end_date?: Date;
    /**
     * The amount the bid incremented by
     */
    bid_increment: number;
    /**
     * The amount the bid incremented by in cent
     */
    bid_increment_cents?: number;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * The currency the user bid in
     */
    currency: string;
    /**
     * Name of the featured team of the event
     */
    event_featured_team_name?: string;
    /**
     * Id of event related to the product that the user bid on
     */
    event_id?: string;
    /**
     * Name of event related to the product that the user bid on
     */
    event_name?: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * Number of user active cards
     */
    number_active_cards: number;
    /**
     * Id of partner related to the product that the user bid on
     */
    partner_id: string;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Id of product that the user bid on
     */
    product_id: string;
    /**
     * Name of product that the user bid on
     */
    product_name: string;
    /**
     * The price of the bid (related to the currency)
     */
    product_price: number;
    /**
     * Price of the product in cents
     */
    product_price_cents?: number;
    /**
     * The price of the product in euros
     */
    product_price_euro: number;
    /**
     * Price of the product in cents (euros cents)
     */
    product_price_euro_cents?: number;
    /**
     * Whether the user needs to pre-authorize a card reservation
     */
    reservation_required: boolean;
    /**
     * Segement spec name for this event (used for automatic mapping to GA4/Meta). Always set to
     * "Product Added"
     */
    segment_spec_name: SegmentSpecName;
    /**
     * Type of bid placed by the user. Can either be BID or QUICKBID
     */
    type: BidClickedType;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Segement spec name for this event (used for automatic mapping to GA4/Meta). Always set to
 * "Product Added"
 */
export type SegmentSpecName = "Product Added";

/**
 * Type of bid placed by the user. Can either be BID or QUICKBID
 */
export type BidClickedType = "BID" | "QUICKBID";

/**
 * Triggered when a user places a bid successfully
 */
export interface BidPlaced {
    /**
     * Bid amount in the bid currency
     */
    bid_amount: number;
    /**
     * Unique identifier for the bid
     */
    bid_id: string;
    /**
     * Categories related to the item
     */
    categories?: BidPlacedCategories;
    /**
     * The category of the relevant content
     */
    category: string;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * The meta content type
     */
    content_type: ContentType;
    /**
     * The content related to meta - this includes an array of objects with properties id &
     * quantity
     */
    contents: any[];
    /**
     * DEPRECATED - Use `categories.country.id` instead
     */
    country_category_id?: string;
    /**
     * DEPRECATED - Use `categories.country.name` instead
     */
    country_category_name?: string;
    /**
     * The currency of the user
     */
    currency:        string;
    event_end_date?: Date;
    /**
     * DEPRECATED - Use `categories.team.name` instead
     */
    event_featured_team_name?: string;
    /**
     * Unique id used by Meta to de-duplicate events. Currently set to the bid_id. THIS IS NOT
     * THE MWS EVENT ID.
     */
    event_id?: string;
    /**
     * Start date for the MWS event
     */
    event_start_date?: Date;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Indicates if the user got outbid immediately by a max bid after placing their bid
     */
    is_outbid?: boolean;
    /**
     * List of items needed for mapping to Google Analytics
     */
    items: BidPlacedItem[];
    /**
     * DEPRECATED - Use `categories.league.id` instead
     */
    league_category_id?: string;
    /**
     * DEPRECATED - Use `categories.league.name` instead
     */
    league_category_name?: string;
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The MWS event the product belongs to
     */
    marketplace_event_id?: string;
    /**
     * Name of the MWS event
     */
    marketplace_event_name?: string;
    /**
     * Name of the relevant content
     */
    name: string;
    /**
     * Id of the event's partner entity (usually the clubs, but not always)
     */
    partner_id?: string;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    product_id:  string;
    /**
     * Labels of the product, comma-separated
     */
    product_labels: string;
    product_name:   string;
    /**
     * Readable identifier of the product. Visible on the URL
     */
    product_slug: string;
    /**
     * Sorting priority of the product. Set in the admin portal
     */
    product_sorting_priority?: number;
    /**
     * DEPRECATED - Use `categories.sport.id` instead
     */
    sport_category_id?: string;
    /**
     * DEPRECATED - Use `categories.sport.name` instead
     */
    sport_category_name?: string;
    /**
     * The price of the product
     */
    value?: number;
    /**
     * A text description combining whether the shirt is Home/Away, whether it's Worn or Issued;
     * and whether it's signed
     */
    variant: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Categories related to the item
 */
export interface BidPlacedCategories {
    /**
     * The related athlete category
     */
    athlete?: FluffyAthlete;
    /**
     * The related country category
     */
    country?: FluffyCountry;
    /**
     * The related league category
     */
    league?: FluffyLeague;
    /**
     * The related other category
     */
    other?: FluffyOther;
    /**
     * The related sport category
     */
    sport?: FluffySport;
    /**
     * The related team category
     */
    team?: FluffyTeam;
    [property: string]: any;
}

/**
 * The related athlete category
 */
export interface FluffyAthlete {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related country category
 */
export interface FluffyCountry {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related league category
 */
export interface FluffyLeague {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related other category
 */
export interface FluffyOther {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related sport category
 */
export interface FluffySport {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related team category
 */
export interface FluffyTeam {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The meta content type
 */
export type ContentType = "product" | "product_group";

/**
 * Item giving information about the product mapping to Google Analytics
 */
export interface BidPlacedItem {
    /**
     * Currency in which the bid was placed
     */
    currency: string;
    /**
     * Order of the product in the list
     */
    index: number;
    /**
     * The name of the featured team from the event
     */
    item_brand?: string;
    /**
     * The name of the related sports of the product
     */
    item_category?: string;
    /**
     * The name of the related country of the product
     */
    item_category2?: string;
    /**
     * The name of the related league of the product
     */
    item_category3?: string;
    /**
     * Event name
     */
    item_category4?: string;
    /**
     * Product id
     */
    item_id: string;
    /**
     * Event id
     */
    item_list_id?: string;
    /**
     * Event name
     */
    item_list_name?: string;
    /**
     * Product name
     */
    item_name: string;
    /**
     * Bid amount in the currency of the placed bid
     */
    price: number;
    [property: string]: any;
}

/**
 * Triggered when a user clicks the buy button on a buy now product
 */
export interface BuyNow {
    /**
     * Always set to euros
     */
    currency: Currency;
    /**
     * Name of the player related to the product
     */
    player_name?: string;
    /**
     * Product price in euros
     */
    price: number;
    /**
     * The technical Id of the product
     */
    product_id: string;
    /**
     * The name of the product
     */
    product_name: string;
    /**
     * Url for the photo of the product
     */
    product_photo: string;
    /**
     * Always set to 1
     */
    quantity: number;
    /**
     * Name of the user who started the framing session
     */
    user_name?: string;
    [property: string]: any;
}

/**
 * Always set to euros
 */
export type Currency = "EUR";

/**
 * Triggered when the user click on a call to action block
 */
export interface CallToActionBlockClicked {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The position of the call to auction in the carousel
     */
    position?: number;
    /**
     * The url the user gets redirected to after clicking on the call to action block
     */
    target_url?: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered when a user clicks a card. For example a team card on the the homepage
 */
export interface CardBlockClicked {
    /**
     * The position of the card in the slider
     */
    card_position?: number;
    /**
     * The url that the card links too.
     */
    redirected_url: string;
    [property: string]: any;
}

/**
 * Triggered when Adyen returns an error while user is trying to tokenize a card
 */
export interface CardTokenizationFailed {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Error code of Adyen's error response
     */
    tokenization_failed_reason?: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered when a user clicks a category
 */
export interface CategoryClicked {
    /**
     * Technical ID of the category
     */
    category_id: string;
    /**
     * The name of the category
     */
    category_name: string;
    /**
     * The type of the category
     */
    category_type: CategoryClickedCategoryType;
    [property: string]: any;
}

/**
 * The type of the category
 *
 * The category related to the menu item
 */
export type CategoryClickedCategoryType = "Athlete" | "Condition" | "Country" | "Gender" | "Highlight" | "League" | "Other" | "Sport" | "Team" | "athlete" | "condition" | "country" | "gender" | "highlight" | "league" | "other" | "sport" | "team";

/**
 * Triggered when an category is followed
 */
export interface CategoryFollowed {
    /**
     * The id of the category which was followed
     */
    category_id: string;
    /**
     * The name of the category which was followed
     */
    category_name?: string;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Source of where the user followed the category (Possible Options: "categoryPage",
     * "onboarding", "categoryListBlock")
     */
    source: CategoryFollowedSource;
    /**
     * Name of the sport related to the category
     */
    sport_category?: string;
    /**
     * Id of the sport related to the category
     */
    sport_category_id?: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Source of where the user followed the category (Possible Options: "categoryPage",
 * "onboarding", "categoryListBlock")
 *
 * Source of where the user unfollowed the category (Possible Options: "categoryPage",
 * "onboarding", "categoryListBlock")
 */
export type CategoryFollowedSource = "categoryListBlock" | "categoryPage" | "onboarding";

/**
 * Triggered when a category is unfollowed
 */
export interface CategoryUnfollowed {
    /**
     * Id of the category
     */
    category_id: string;
    /**
     * Name of the category
     */
    category_name?: string;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Source of where the user unfollowed the category (Possible Options: "categoryPage",
     * "onboarding", "categoryListBlock")
     */
    source: CategoryFollowedSource;
    /**
     * Name of the sport related to the category
     */
    sport_category?: string;
    /**
     * Id of the sport related to the category
     */
    sport_category_id?: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered when a category details page/screen is visited
 */
export interface CategoryViewed {
    /**
     * The id of the visited category
     */
    category_id: string;
    /**
     * The name of the visited category
     */
    category_name?: string;
    /**
     * The type of the visited category (e.g. Team, League, Sports etc.)
     */
    category_type?: CategoryViewedCategoryType;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    [property: string]: any;
}

/**
 * The type of the visited category (e.g. Team, League, Sports etc.)
 */
export type CategoryViewedCategoryType = "athlete" | "condition" | "country" | "gender" | "highlight" | "league" | "other" | "sport" | "team";

export interface CheckoutStarted {
    category?: string;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel:   Channel;
    currency?: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * Technical id of the order
     */
    order_id?: string;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    products?:   CheckoutStartedProduct[];
    revenue?:    number;
    shipping?:   number;
    tax?:        number;
    value?:      number;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

export interface CheckoutStartedProduct {
    categories?:   ProductCategories;
    category?:     string;
    framing_type?: FramingType;
    image_url?:    string;
    name?:         string;
    position?:     number;
    price?:        number;
    product_id?:   string;
    quantity?:     number;
    sku?:          string;
    url?:          string;
    [property: string]: any;
}

export interface ProductCategories {
    athlete?: TentacledAthlete;
    country?: TentacledCountry;
    league?:  TentacledLeague;
    other?:   TentacledOther;
    sport?:   TentacledSport;
    team?:    TentacledTeam;
    [property: string]: any;
}

export interface TentacledAthlete {
    id:   string;
    name: string;
    [property: string]: any;
}

export interface TentacledCountry {
    id:   string;
    name: string;
    [property: string]: any;
}

export interface TentacledLeague {
    id:   string;
    name: string;
    [property: string]: any;
}

export interface TentacledOther {
    id:   string;
    name: string;
    [property: string]: any;
}

export interface TentacledSport {
    id:   string;
    name: string;
    [property: string]: any;
}

export interface TentacledTeam {
    id:   string;
    name: string;
    [property: string]: any;
}

export type FramingType = "Frameable" | "Framed" | "NotFrameable" | "frameable" | "framed" | "not_frameable";

/**
 * Triggered when a user clicks on a recently viewed product card
 */
export interface ClickedRecentlyViewedProduct {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * User-specified currency, in which the product price is tracked
     */
    currency: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Price of the product, in the user-specified currency
     */
    price: number;
    /**
     * Id of the clicked product
     */
    product_id: string;
    /**
     * Name of the clicked product
     */
    product_name: string;
    /**
     * The url of the product details page - restricted to max 100 characters
     */
    url?: string;
    /**
     * The labels of the product, separated by " - "
     */
    variant?: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered when a user confirms their bid
 */
export interface ConfirmBidClicked {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * Amount of active cards the user has (not processing)
     */
    number_active_cards?: number;
    /**
     * Whether the user has given consent to MWS parters using their data for marketing purposes
     */
    partner_marketing_consent?: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Whether card reservation (pre-authorisation) is required for this bid
     */
    reservation_required: boolean;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered when the user clicks on the action to delete the card
 */
export interface DeleteCardClicked {
    /**
     * Whether the card the user is trying to delete is active or processing
     */
    card_status: CardStatus;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Whether the card the user is trying to delete is default or not
     */
    is_default: boolean;
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * Amount of active cards/tokens the user has (not processing)
     */
    number_active_cards: number;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The location from where the delete action was triggered:
     * 'product_page'/'bidding'/'profile'
     */
    source: AddNewCardClickedSource;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Whether the card the user is trying to delete is active or processing
 */
export type CardStatus = "active" | "processing";

/**
 * Triggered when a user verifies their email address
 */
export interface EmailVerified {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    [property: string]: any;
}

/**
 * Triggered when an event link on the header part of a product slider is clicked
 */
export interface EventClicked {
    auction_end_date:            Date;
    auction_featured_team_name?: string;
    auction_id:                  string;
    /**
     * Position of the event in the list where it was shown
     */
    auction_list_position?: number;
    auction_name:           string;
    auction_start_date:     Date;
    /**
     * The url path in which the user has clicked the event link
     */
    source_path?: string;
    [property: string]: any;
}

/**
 * Triggered when an event is followed
 */
export interface EventFollowed {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * Id of event that is followed
     */
    event_id: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Sport name related to the category followed
     */
    sport_category?: string;
    /**
     * Sport id related to the category followed
     */
    sport_category_id?: string;
    /**
     * Type of where the user followed from (Either "default" or "easy")
     */
    type: EventFollowedType;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Type of where the user followed from (Either "default" or "easy")
 *
 *
 * Type of where the user unfollowed from (Either "default" or "easy")
 *
 *
 * Follow type
 *
 * The method which the user followed
 */
export type EventFollowedType = "automatic" | "default" | "easy";

/**
 * Triggered when the event name on a product page is clicked, redirecting the user to the
 * event page
 */
export interface EventNameClicked {
    event:          EventNameClickedEvent;
    featured_team?: EventNameClickedFeaturedTeam;
    product:        EventNameClickedProduct;
    [property: string]: any;
}

export interface EventNameClickedEvent {
    id:   string;
    name: string;
    [property: string]: any;
}

export interface EventNameClickedFeaturedTeam {
    id:   string;
    name: string;
    [property: string]: any;
}

export interface EventNameClickedProduct {
    id:   string;
    name: string;
    [property: string]: any;
}

/**
 * Triggered when an event is unfollowed
 */
export interface EventUnfollowed {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * Id of event that is unfollowed
     */
    event_id: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Type of where the user unfollowed from (Either "default" or "easy")
     */
    type: EventFollowedType;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered when a event details page/screen is visited
 */
export interface EventViewed {
    /**
     * Categories related to the item. Only one category is provided for each type. For products
     * and auctions, they will be based on the directly connected categories. For categories,
     * they will be based on the other categories that its auctions are linked to (For example
     * the team category Liverpool will have the related sport category "Football" because its
     * events will have the "Football" category linked to them. Similarly, Liverpool may have
     * "Poppy Appeal" as the related other category since one of its events may be linked to it).
     */
    categories?: EventViewedCategories;
    /**
     * The marketplace auction end date
     */
    end_date: Date;
    /**
     * The marketplace auction event id
     */
    event_id: string;
    /**
     * The marketplace auction name
     */
    event_name: string;
    [property: string]: any;
}

/**
 * Categories related to the item. Only one category is provided for each type. For products
 * and auctions, they will be based on the directly connected categories. For categories,
 * they will be based on the other categories that its auctions are linked to (For example
 * the team category Liverpool will have the related sport category "Football" because its
 * events will have the "Football" category linked to them. Similarly, Liverpool may have
 * "Poppy Appeal" as the related other category since one of its events may be linked to it).
 */
export interface EventViewedCategories {
    /**
     * The related athlete category
     */
    athlete?: StickyAthlete;
    /**
     * The related country category
     */
    country?: StickyCountry;
    /**
     * The related league category
     */
    league?: StickyLeague;
    /**
     * The related other category
     */
    other?: StickyOther;
    /**
     * The related sport category
     */
    sport?: StickySport;
    /**
     * The related team category
     */
    team?: StickyTeam;
    [property: string]: any;
}

/**
 * The related athlete category
 */
export interface StickyAthlete {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related country category
 */
export interface StickyCountry {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related league category
 */
export interface StickyLeague {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related other category
 */
export interface StickyOther {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related sport category
 */
export interface StickySport {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related team category
 */
export interface StickyTeam {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * Triggered when the user enters an A/B test
 */
export interface ExperimentViewed {
    /**
     * The identifier of the experiment
     */
    experimentId: string;
    /**
     * The result of the experiment
     */
    variationId: string;
    [property: string]: any;
}

/**
 * Triggered when a user views the fabricks authentication page
 */
export interface FabricksAuthenticationPageViewed {
    authenticated_by_partner_name?: string;
    /**
     * The name of the fixture related to the product
     */
    fixture_name?: string;
    /**
     * The name of the league related to the product
     */
    league_name?: string;
    mws_label:    string[];
    /**
     * The name of the player
     */
    player_name?: string;
    /**
     * The name of the product
     */
    product_name?: string;
    product_type?: string;
    /**
     * The season the product is from
     */
    season_name?:          string;
    sold_by_partner_name?: string;
    trackable_product_id:  string;
    [property: string]: any;
}

/**
 * Triggered when the user completes a screen in the favourites onboarding flow
 */
export interface FavouritesOnboardingScreenCompleted {
    /**
     * How many times the user has seen the favourites onboarding flow
     */
    attempt_no: number;
    /**
     * The categories the user followed as a part of that screen
     */
    followed_items: string[];
    /**
     * Name of the completed screen
     */
    screen_name: string;
    [property: string]: any;
}

/**
 * Triggered when the user skips the favourites onboarding flow
 */
export interface FavouritesOnboardingSkipped {
    /**
     * How many times the user has seen the favourites onboarding flow
     */
    attempt_no: number;
    /**
     * The step from which the user skipped flow
     */
    current_step: string;
    [property: string]: any;
}

/**
 * Triggered when a user is shown the favourites onboarding flow
 */
export interface FavouritesOnboardingStarted {
    /**
     * How many times the user has seen the favourites onboarding flow
     */
    attempt_no: number;
    /**
     * The original source of how the Favourites Onboarding opened
     */
    source?: FavouritesOnboardingStartedSource;
    [property: string]: any;
}

/**
 * The original source of how the Favourites Onboarding opened
 */
export type FavouritesOnboardingStartedSource = "ACCOUNT_CREATION_POPUP" | "CLUB_SSO" | "FAVOURITES" | "FOLLOWING" | "GIVEAWAY" | "LOGIN" | "NEW_SESSION" | "ONE_TAP";

/**
 * Triggered when the filter button is clicked
 */
export interface FilterButtonClicked {
    /**
     * The page the user clicked the button on
     */
    page_type: FilterButtonClickedPageType;
    [property: string]: any;
}

/**
 * The page the user clicked the button on
 */
export type FilterButtonClickedPageType = "categoryPage" | "eventDetailsPage" | "searchEventsPage" | "searchProductsPage";

/**
 * Triggered when the user applies filters
 */
export interface FiltersApplied {
    /**
     * A list of the athlete filter options applied
     */
    athlete_filter_options?: string[];
    /**
     * A list of the condition filter options applied
     */
    condition_filter_options?: string[];
    /**
     * A list of the country filter options applied
     */
    country_filter_options?: string[];
    /**
     * A list of the event filter options applied
     */
    event_filter_options?: string[];
    /**
     * A list of the filters types applied
     */
    filters: string[];
    /**
     * A list of the gender filter options applied
     */
    gender_filter_options?: string[];
    /**
     * A list of the highlight filter options applied
     */
    highlight_filter_options?: string[];
    /**
     * The value of the isSigned filter option applied
     */
    issigned_filter_option?: string;
    /**
     * A list of the league filter options applied
     */
    league_filter_options?: string[];
    /**
     * The latest applied filter option
     */
    new_applied_filter_option?: string;
    /**
     * The latest applied filter type
     */
    new_applied_filter_type?: string;
    /**
     * The page which the user is filtering on
     */
    page_type: FiltersAppliedPageType;
    /**
     * The way the user is sorting
     */
    sort?: string;
    /**
     * A list of the sport filter options applied
     */
    sport_filter_options?: string[];
    /**
     * A list of the team filter options applied
     */
    team_filter_options?: string[];
    /**
     * A list of the product type filter options applied
     */
    type_filter_options?: string[];
    [property: string]: any;
}

/**
 * The page which the user is filtering on
 *
 * The page the users on
 */
export type FiltersAppliedPageType = "allProductsPage" | "categoryPage" | "eventDetailsPage" | "searchEventsPage" | "searchProductsPage";

/**
 * Triggered when the user clears their filters
 */
export interface FiltersCleared {
    /**
     * The clear all pill removed all filters
     */
    clear_all?: boolean;
    /**
     * The list of filter option(s) that was just cleared
     */
    filter_options?: string[];
    /**
     * The list of filter type(s) that was just cleared
     */
    filter_types?: string[];
    /**
     * The page the users on
     */
    page_type: FiltersAppliedPageType;
    [property: string]: any;
}

/**
 * Triggered when a user selects a framing preset
 */
export interface FramingPresetSelected {
    /**
     * Technical identifier of the preset
     */
    preset_id: string;
    /**
     * Technical identifier of the product
     */
    product_id: string;
    [property: string]: any;
}

/**
 * Triggered when the user selects "customize" after winning a shirt
 */
export interface FramingSessionStarted {
    /**
     * Name of the player related to the product
     */
    player_name?: string;
    preset_id?:   string;
    /**
     * Price of the order
     */
    price: number;
    /**
     * Technical id of the product
     */
    product_id: string;
    /**
     * Name of the product
     */
    product_name?: string;
    /**
     * Url for the photo of the product
     */
    product_photo: string;
    quantity:      number;
    /**
     * Name of the user who started the framing session
     */
    user_name?: string;
    [property: string]: any;
}

/**
 * Triggered when the user enters the framing upsell flow
 */
export interface FramingUpsellClicked {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * The discount percentage that was given to the user for this order
     */
    discount_percentage: number;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * The price of the original item
     */
    item_price: FramingUpsellClickedItemPrice;
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the original order
     */
    order_id: string;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * The price of the original item
 */
export interface FramingUpsellClickedItemPrice {
    currency?: string;
    price?:    number;
    [property: string]: any;
}

/**
 * Triggered when the button is clicked inside of the Full Width Content block
 */
export interface FullWidthContentClicked {
    /**
     * Source path of the action
     */
    source: string;
    /**
     * Target path of the action
     */
    target: string;
    [property: string]: any;
}

/**
 * Triggered when a user joins a giveaway
 */
export interface GiveawayJoinButtonClicked {
    /**
     * The meta data configured in Storyblok (like a unique id for the giveaway)
     */
    giveaway_meta_data: string;
    /**
     * The slug of the giveaway (Url Path)
     */
    giveaway_slug: string;
    [property: string]: any;
}

/**
 * Triggered when a user clicks on a highlighted menu link
 */
export interface HighlightedMenuLinkClicked {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * The position of the highlighted menu link clicked by the user in all displayed
     * highlighted menu links
     */
    highlight_position: number;
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The url the user gets redirected to after clicking on the highlighted menu link
     */
    target_url: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered whenever a league name works as a redirect link to a category screen
 */
export interface LeaguePressed {
    /**
     * The name of the event which belongs to the league
     */
    event_name?: string;
    /**
     * The name of the league
     */
    league_name: string;
    /**
     * The screen origin where the pressable league link is located
     */
    origin: Origin;
    /**
     * The name of the product which belongs to the league
     */
    product_name?: string;
    [property: string]: any;
}

/**
 * The screen origin where the pressable league link is located
 */
export type Origin = "event" | "product";

/**
 * Triggered when the user clicks the login button
 */
export interface LoginClicked {
    /**
     * The auth provider the user used
     */
    provider: string;
    [property: string]: any;
}

/**
 * Triggered when the login form is viewed on a dialog or on the login page
 */
export interface LoginFormViewed {
    /**
     * Whether this event was triggered from the website or the mobile app
     */
    channel: Channel;
    /**
     * The action by which the login dialog/page was triggered
     */
    onboarding_starting_point: StartingPoint;
    [property: string]: any;
}

/**
 * The action by which the login dialog/page was triggered
 *
 * What was the user doing before logging in
 *
 * The starting action the user took to open the form
 */
export type StartingPoint = "ACCOUNT_CREATION_POPUP" | "BIDDING" | "CLUB_SSO" | "FAVOURITES" | "GIVE_AWAY" | "LOGIN" | "ONE_TAP";

/**
 * Triggered when a user has successfully logged into their account
 */
export interface LoginSucceeded {
    /**
     * The list of the users identity providers
     */
    identity_providers: any[];
    /**
     * What was the user doing before logging in
     */
    onboarding_starting_point: StartingPoint;
    [property: string]: any;
}

/**
 * Triggered when the event logo on a product page is clicked, redirecting the user to the
 * event page
 */
export interface LogoPressed {
    event:          LogoPressedEvent;
    featured_team?: LogoPressedFeaturedTeam;
    product:        LogoPressedProduct;
    [property: string]: any;
}

export interface LogoPressedEvent {
    id:   string;
    name: string;
    [property: string]: any;
}

export interface LogoPressedFeaturedTeam {
    id:   string;
    name: string;
    [property: string]: any;
}

export interface LogoPressedProduct {
    id:   string;
    name: string;
    [property: string]: any;
}

/**
 * Triggered when a user tries to place a max bid on an item
 */
export interface MaxBidClicked {
    /**
     * The end date of the related auction event.
     */
    auction_end_date?: Date;
    /**
     * The amount the bid incremented by
     */
    bid_increment: number;
    /**
     * The amount the bid incremented by in cent
     */
    bid_increment_cents?: number;
    /**
     * The currency the user bid in
     */
    currency: string;
    /**
     * Name of the featured team of the event
     */
    event_featured_team_name?: string;
    /**
     * Id of event related to the product that the user bid on
     */
    event_id?: string;
    /**
     * Name of event related to the product that the user bid on
     */
    event_name?: string;
    /**
     * The amount of the max bid
     */
    max_bid_amount: number;
    /**
     * The amount of the max bid in cents
     */
    max_bid_amount_cents?: number;
    /**
     * Number of user active cards
     */
    number_active_cards: number;
    /**
     * Id of partner related to the product that the user bid on
     */
    partner_id: string;
    /**
     * Id of product that the user bid on
     */
    product_id: string;
    /**
     * Name of product that the user bid on
     */
    product_name: string;
    /**
     * The price of the bid (related to the currency)
     */
    product_price: number;
    /**
     * Price of the product in cents
     */
    product_price_cents?: number;
    /**
     * The price of the product in euros
     */
    product_price_euro: number;
    /**
     * Price of the product in cents (euros cents)
     */
    product_price_euro_cents?: number;
    /**
     * Whether the user needs to pre-authorize a card reservation
     */
    reservation_required: boolean;
    /**
     * Segment spec name for this event (used for automatic mapping to GA4/Meta). Always set to
     * "Product Added"
     */
    segment_spec_name: SegmentSpecName;
    [property: string]: any;
}

/**
 * Triggered when a user places a bid successfully
 */
export interface MaxBidPlaced {
    /**
     * Bid amount in the bid currency
     */
    bid_amount: number;
    /**
     * Unique identifier for the bid
     */
    bid_id: string;
    /**
     * Categories related to the item
     */
    categories?: MaxBidPlacedCategories;
    /**
     * The category of the relevant content
     */
    category: string;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * The meta content type
     */
    content_type: ContentType;
    /**
     * The content related to meta - this includes an array of objects with properties id &
     * quantity
     */
    contents: any[];
    /**
     * DEPRECATED - Use `categories.country.id` instead
     */
    country_category_id?: string;
    /**
     * DEPRECATED - Use `categories.country.name` instead
     */
    country_category_name?: string;
    /**
     * The currency of the user
     */
    currency:        string;
    event_end_date?: Date;
    /**
     * DEPRECATED - Use `categories.team.name` instead
     */
    event_featured_team_name?: string;
    /**
     * Unique id used by Meta to de-duplicate events. Currently set to the bid_id. THIS IS NOT
     * THE MWS EVENT ID.
     */
    event_id?: string;
    /**
     * Start date for the MWS event
     */
    event_start_date?: Date;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Indicates if the user got outbid immediately by a max bid after placing their bid
     */
    is_outbid: boolean;
    /**
     * List of items needed for mapping to Google Analytics
     */
    items: MaxBidPlacedItem[];
    /**
     * DEPRECATED - Use `categories.league.id` instead
     */
    league_category_id?: string;
    /**
     * DEPRECATED - Use `categories.league.name` instead
     */
    league_category_name?: string;
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The MWS event the product belongs to
     */
    marketplace_event_id?: string;
    /**
     * Name of the MWS event
     */
    marketplace_event_name?: string;
    /**
     * Name of the relevant content
     */
    name: string;
    /**
     * Id of the event's partner entity (usually the clubs, but not always)
     */
    partner_id?: string;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    product_id:  string;
    /**
     * Labels of the product, comma-separated
     */
    product_labels: string;
    product_name:   string;
    /**
     * Readable identifier of the product. Visible on the URL
     */
    product_slug: string;
    /**
     * Sorting priority of the product. Set in the admin portal
     */
    product_sorting_priority?: number;
    /**
     * DEPRECATED - Use `categories.sport.id` instead
     */
    sport_category_id?: string;
    /**
     * DEPRECATED - Use `categories.sport.name` instead
     */
    sport_category_name?: string;
    /**
     * The price of the product
     */
    value?: number;
    /**
     * A text description combining whether the shirt is Home/Away, whether it's Worn or Issued;
     * and whether it's signed
     */
    variant: string;
    [property: string]: any;
}

/**
 * Categories related to the item
 */
export interface MaxBidPlacedCategories {
    /**
     * The related athlete category
     */
    athlete?: IndigoAthlete;
    /**
     * The related country category
     */
    country?: IndigoCountry;
    /**
     * The related league category
     */
    league?: IndigoLeague;
    /**
     * The related other category
     */
    other?: IndigoOther;
    /**
     * The related sport category
     */
    sport?: IndigoSport;
    /**
     * The related team category
     */
    team?: IndigoTeam;
    [property: string]: any;
}

/**
 * The related athlete category
 */
export interface IndigoAthlete {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related country category
 */
export interface IndigoCountry {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related league category
 */
export interface IndigoLeague {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related other category
 */
export interface IndigoOther {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related sport category
 */
export interface IndigoSport {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related team category
 */
export interface IndigoTeam {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * Item giving information about the product mapping to Google Analytics
 */
export interface MaxBidPlacedItem {
    /**
     * Currency in which the bid was placed
     */
    currency: string;
    /**
     * Order of the product in the list
     */
    index: number;
    /**
     * The name of the featured team from the event
     */
    item_brand?: string;
    /**
     * The name of the related sports of the product
     */
    item_category?: string;
    /**
     * The name of the related country of the product
     */
    item_category2?: string;
    /**
     * The name of the related league of the product
     */
    item_category3?: string;
    /**
     * Event name
     */
    item_category4?: string;
    /**
     * Product id
     */
    item_id: string;
    /**
     * Event id
     */
    item_list_id?: string;
    /**
     * Event name
     */
    item_list_name?: string;
    /**
     * Product name
     */
    item_name: string;
    /**
     * Bid amount in the currency of the placed bid
     */
    price: number;
    [property: string]: any;
}

/**
 * Triggered when a menu item is clicked within a menu hierarchy
 */
export interface MenuItemClicked {
    /**
     * The name of the menu item
     */
    item_name: string;
    /**
     * The category type of the menu item
     */
    item_type: string;
    [property: string]: any;
}

/**
 * Triggered when a user signs up for the newsletter
 */
export interface NewsletterFormSubmitted {
    /**
     * The category selected for the newsletter
     */
    category_id: string;
    [property: string]: any;
}

export interface NotificationClicked {
    destination_url?:             string;
    notification_delivered_date?: string;
    /**
     * Position in the list in the notification center
     */
    notification_position?: number;
    notification_status?:   NotificationStatus;
    /**
     * Type of notification is currently filled with the braze campaign ID, as we do not have
     * access to a descriptive name
     */
    notification_type?: string;
    [property: string]: any;
}

export type NotificationStatus = "read" | "unread";

export interface NotificationsMarkedAllAsRead {
    total_unread?: number;
    [property: string]: any;
}

/**
 * Triggered when the notifications menu/screen is viewed by the user
 */
export interface NotificationsViewed {
    page_type?:                  string;
    total_notifications?:        number;
    total_notifications_unread?: number;
    [property: string]: any;
}

/**
 * Triggered when a user clicks a menu item from the now live tab
 */
export interface NowLiveItemClicked {
    /**
     * The name of the menu item
     */
    category_name?: string;
    /**
     * The category related to the menu item
     */
    category_type?: CategoryClickedCategoryType;
    /**
     * Position in the list displayed to the user
     */
    position?: number;
    [property: string]: any;
}

/**
 * Triggered when the user clicks on the now live tab
 */
export interface NowLiveViewed {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    [property: string]: any;
}

/**
 * Triggered when a user returns to the site successfully completing their order
 */
export interface OrderCompleted {
    /**
     * The athlete category of the first product in "Items" array
     */
    category?: string;
    /**
     * The technical id of the users checkout instance
     */
    checkout_id?: string;
    /**
     * The currency of the order
     */
    currency?: string;
    /**
     * Value used by Meta to de-duplicate events from multiple sources. We will send the order
     * id here.
     */
    event_id?: string;
    /**
     * The name of the product
     */
    name?: string;
    /**
     * The technical id of the users order
     */
    order_id?: string;
    /**
     * The products in the users cart
     */
    products?: OrderCompletedProduct[];
    /**
     * The revenue of the order
     */
    revenue?: number;
    /**
     * The shipping cost
     */
    shipping?: number;
    /**
     * The subtotal of the users cart
     */
    subtotal?: number;
    /**
     * The amount of tax of the order
     */
    tax?: number;
    /**
     * The total of the users cart
     */
    total?: number;
    [property: string]: any;
}

export interface OrderCompletedProduct {
    /**
     * The category related to the product
     */
    category?: string;
    /**
     * The image url to the product
     */
    image_url?: string;
    /**
     * The name of the product
     */
    name?: string;
    /**
     * The position of the product in the cart
     */
    position?: number;
    /**
     * The price of the product
     */
    price?: number;
    /**
     * The technical id of the product
     */
    product_id?: string;
    /**
     * The quantity of the product
     */
    quantity?: number;
    /**
     * The stock keeping unit
     */
    sku?: string;
    /**
     * The page url of the product
     */
    url?: string;
    [property: string]: any;
}

/**
 * When the user enters the payment success page after purchasing
 */
export interface OrderPaid {
    /**
     * Categories related to the item
     */
    categories?: OrderPaidCategories;
    /**
     * The category of the relevant content
     */
    category: string;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * The meta content type
     */
    content_type: ContentType;
    /**
     * The content related to meta - this includes an array of objects with properties id &
     * quantity
     */
    contents: any[];
    /**
     * The currency of the user
     */
    currency: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when a paid order is an upsell
     */
    is_upsell: boolean;
    /**
     * Items related to an order
     */
    items?: OrderPaidItem[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * Name of the relevant content
     */
    name: string;
    /**
     * The Id of the order
     */
    order_id: string;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The shipping cost
     */
    shipping?: number;
    /**
     * The price of the product
     */
    value?: number;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Categories related to the item
 */
export interface OrderPaidCategories {
    /**
     * The related athlete category
     */
    athlete?: IndecentAthlete;
    /**
     * The related country category
     */
    country?: IndecentCountry;
    /**
     * The related league category
     */
    league?: IndecentLeague;
    /**
     * The related other category
     */
    other?: IndecentOther;
    /**
     * The related sport category
     */
    sport?: IndecentSport;
    /**
     * The related team category
     */
    team?: IndecentTeam;
    [property: string]: any;
}

/**
 * The related athlete category
 */
export interface IndecentAthlete {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related country category
 */
export interface IndecentCountry {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related league category
 */
export interface IndecentLeague {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related other category
 */
export interface IndecentOther {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related sport category
 */
export interface IndecentSport {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related team category
 */
export interface IndecentTeam {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

export interface OrderPaidItem {
    /**
     * The currency of the item
     */
    currency?: string;
    /**
     * Name of the event related to the product
     */
    event_name?: string;
    /**
     * Index in the list
     */
    index: number;
    /**
     * The id of the item
     */
    item_id: string;
    /**
     * The name of the item
     */
    item_name?: string;
    /**
     * The price of the item related to the currency
     */
    price?: number;
    /**
     * The quantity of items
     */
    quantity: number;
    /**
     * The variant of the item
     */
    variant?: string;
    [property: string]: any;
}

/**
 * Triggered when a page is viewed on the website
 */
export interface PageViewed {
    /**
     * The name of the athlete related to the page (If applicable)
     */
    athlete_name?: string;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * Name of the country related to the page
     */
    country_name?: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Name of the league related to the page
     */
    league_name?: string;
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The  other category related to the page (If applicable)
     */
    other_category?: string;
    /**
     * Name of the page related to the type (For example: "my-account")
     */
    page_type?: string;
    params?:    { [key: string]: any };
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Promotion category related to the page viewed
     */
    promotion_category?: string;
    /**
     * The raw path of the page (including localisation). For example for a product page
     * `/product/product_slug`
     */
    raw_path?: string;
    /**
     * Sport category related to the page viewed
     */
    sport_category?: string;
    /**
     * Name of the team related to the page
     */
    team_name?: string;
    /**
     * The raw path of the page (excluding localisation). For example for a product page
     * `/product/product_slug`
     */
    unlocalized_path?: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered when the users clicks the button to pay after entering their payment details
 */
export interface PayButtonClicked {
    /**
     * Categories related to the item. Only one category is provided for each type. For products
     * and auctions, they will be based on the directly connected categories. For categories,
     * they will be based on the other categories that its auctions are linked to (For example
     * the team category Liverpool will have the related sport category "Football" because its
     * events will have the "Football" category linked to them. Similarly, Liverpool may have
     * "Poppy Appeal" as the related other category since one of its events may be linked to it).
     */
    categories?: PayButtonClickedCategories;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * The currency of the purchase
     */
    currency?: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Items related to an order
     */
    items?: PayButtonClickedItem[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The method which the user used to pay
     */
    payment_type: string;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The amount of the purchase
     */
    value: number;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Categories related to the item. Only one category is provided for each type. For products
 * and auctions, they will be based on the directly connected categories. For categories,
 * they will be based on the other categories that its auctions are linked to (For example
 * the team category Liverpool will have the related sport category "Football" because its
 * events will have the "Football" category linked to them. Similarly, Liverpool may have
 * "Poppy Appeal" as the related other category since one of its events may be linked to it).
 */
export interface PayButtonClickedCategories {
    /**
     * The related athlete category
     */
    athlete?: HilariousAthlete;
    /**
     * The related country category
     */
    country?: HilariousCountry;
    /**
     * The related league category
     */
    league?: HilariousLeague;
    /**
     * The related other category
     */
    other?: HilariousOther;
    /**
     * The related sport category
     */
    sport?: HilariousSport;
    /**
     * The related team category
     */
    team?: HilariousTeam;
    [property: string]: any;
}

/**
 * The related athlete category
 */
export interface HilariousAthlete {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related country category
 */
export interface HilariousCountry {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related league category
 */
export interface HilariousLeague {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related other category
 */
export interface HilariousOther {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related sport category
 */
export interface HilariousSport {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related team category
 */
export interface HilariousTeam {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

export interface PayButtonClickedItem {
    /**
     * The currency of the item
     */
    currency?: string;
    /**
     * Name of the event related to the product
     */
    event_name?: string;
    /**
     * The framing type of the item
     */
    framing_type?: string;
    /**
     * Index in the list
     */
    index: number;
    /**
     * The id of the item
     */
    item_id: string;
    /**
     * The name of the item
     */
    item_name?: string;
    /**
     * The price of the item related to the currency
     */
    price?: number;
    /**
     * The quantity of items
     */
    quantity: number;
    /**
     * The variant of the item
     */
    variant?: string;
    [property: string]: any;
}

/**
 * Triggered when the user clicks on 'Select card and continue' — agreeing to make a
 * reservation
 */
export interface PreAuthCreditCardSelected {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The amount of active cards/tokens the user has (not processing)
     */
    number_active_cards: number;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Triggered when a user leaves the onboarding fow
 */
export interface PreferencesOnboardingFlowExited {
    /**
     * The name of the step which the user exited on
     */
    exit_step_name: string;
    /**
     * The step number of which the user exited on
     */
    exit_step_number: number;
    [property: string]: any;
}

/**
 * Triggered when the user sees the onboarding modal
 */
export interface PreferencesOnboardingModalOpened {
    /**
     * What has triggered the user to see the onboarding modal
     */
    opened_by: OpenedBy;
    [property: string]: any;
}

/**
 * What has triggered the user to see the onboarding modal
 */
export type OpenedBy = "system" | "user";

/**
 * Triggered when the user opens the image carousel
 */
export interface ProductCarouselOpened {
    /**
     * The active media item when opening the carousel
     */
    active_media_item: string;
    [property: string]: any;
}

/**
 * Triggered when a product card is clicked (and opens product details)
 */
export interface ProductClicked {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel:                   Channel;
    country_category_id?:      string;
    country_category_name?:    string;
    currency:                  string;
    event_end_date?:           Date;
    event_featured_team_name?: string;
    event_id?:                 string;
    /**
     * The position of the event in the list if the product card was being displayed as a part
     * of a list of events in a slider.
     */
    event_list_position?: number;
    event_name?:          string;
    event_start_date?:    Date;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?:  any[];
    highlights?:           string[];
    league_category_id?:   string;
    league_category_name?: string;
    list_id?:              string;
    list_name?:            string;
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in:   boolean;
    partner_id?: string;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id:               string;
    price:                     number;
    price_cents?:              number;
    price_euro?:               number;
    price_euro_cents?:         number;
    product_id:                string;
    product_labels?:           string;
    product_name:              string;
    product_sales_method?:     ProductSalesMethod;
    product_slug?:             string;
    product_sorting_priority?: number;
    /**
     * The block/page where the clicked product card was being displayed
     */
    source?: string;
    /**
     * The url path in which the user has clicked the product card
     */
    source_path?:         string;
    sport_category_id?:   string;
    sport_category_name?: string;
    variant?:             string;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

export type ProductSalesMethod = "bidding" | "fixed_price" | "sweepstakes";

/**
 * Triggered when a product is followed
 */
export interface ProductFollowed {
    /**
     * Categories related to the item
     */
    categories: ProductFollowedCategories;
    /**
     * Category name for Facebook. Will typically be the team name for MatchWornShirt, and the
     * athlete name for FrameTheGame. Prefer using the `categories` field instead
     */
    category?: string;
    /**
     * Content name for Facebook
     */
    content_name: string;
    /**
     * Content type for Facebook (will always be "product")
     */
    content_type: string;
    /**
     * List of contents for Facebook. Will only have one item in this case
     */
    contents: Content[];
    /**
     * Will always be EUR
     */
    currency: string;
    /**
     * Unique value to help Meta de-duplicate this event. Currently set to '$productId_$userId'
     */
    event_id: string;
    /**
     * Current price of the product in Euros
     */
    price?:       number;
    product_id:   string;
    product_name: string;
    /**
     * DEPRECATED - Use `categories.sport.name` instead. Related sport category name.
     */
    sport_category?: string;
    /**
     * DEPRECATED - Use `categories.sport.id` instead. Related sport id category id
     */
    sport_category_id?: string;
    /**
     * Follow type
     */
    type: EventFollowedType;
    /**
     * MWS id of the logged in user
     */
    user_id?: string;
    /**
     * Product price in Euros for Facebook
     */
    value?: number;
    [property: string]: any;
}

/**
 * Categories related to the item
 */
export interface ProductFollowedCategories {
    /**
     * The related athlete category
     */
    athlete?: AmbitiousAthlete;
    /**
     * The related country category
     */
    country?: AmbitiousCountry;
    /**
     * The related league category
     */
    league?: AmbitiousLeague;
    /**
     * The related other category
     */
    other?: AmbitiousOther;
    /**
     * The related sport category
     */
    sport?: AmbitiousSport;
    /**
     * The related team category
     */
    team?: AmbitiousTeam;
    [property: string]: any;
}

/**
 * The related athlete category
 */
export interface AmbitiousAthlete {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related country category
 */
export interface AmbitiousCountry {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related league category
 */
export interface AmbitiousLeague {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related other category
 */
export interface AmbitiousOther {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related sport category
 */
export interface AmbitiousSport {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related team category
 */
export interface AmbitiousTeam {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

export interface Content {
    /**
     * Will always be "EUR"
     */
    currency: string;
    id:       string;
    name:     string;
    quantity: number;
    /**
     * Price in Euros
     */
    value?: number;
    [property: string]: any;
}

/**
 * Triggered when a user selects a different media item on the product screen
 */
export interface ProductImagesCarrousel {
    current_image?: any;
    [property: string]: any;
}

/**
 * Triggered when the user switches between different tabs on the product details screen
 */
export interface ProductTabViewed {
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * The name of the event which the product belongs to
     */
    event_name?: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Name of the product which details are being viewed
     */
    product_name?: string;
    /**
     * Name of the tab, ex: Authentication, Bids, Framing, Proceeds, Product Details, Shipping,
     * Terms and Conditions
     */
    tab_name: TabName;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Name of the tab, ex: Authentication, Bids, Framing, Proceeds, Product Details, Shipping,
 * Terms and Conditions
 */
export type TabName = "Authentication" | "Bids" | "Event" | "Framing" | "No Shipping" | "Proceeds" | "Product Details" | "Shipping" | "Terms and Conditions";

/**
 * Triggered when a user unfollows a product
 */
export interface ProductUnfollowed {
    /**
     * The technical id of the product
     */
    product_id: string;
    /**
     * The method which the user followed
     */
    type: EventFollowedType;
    [property: string]: any;
}

/**
 * Triggered when a user views the product page
 */
export interface ProductViewed {
    /**
     * Categories related to the item. Only one category is provided for each type. For products
     * and auctions, they will be based on the directly connected categories. For categories,
     * they will be based on the other categories that its auctions are linked to (For example
     * the team category Liverpool will have the related sport category "Football" because its
     * events will have the "Football" category linked to them. Similarly, Liverpool may have
     * "Poppy Appeal" as the related other category since one of its events may be linked to it).
     */
    categories?: ProductViewedCategories;
    /**
     * The category of the relevant content
     */
    category: string;
    /**
     * The channel the user has performed the action on. (Either set to "Web" -
     * www.matchwornshirt.com or "Mobile" - Matchwornshirt App).
     */
    channel: Channel;
    /**
     * The meta content type
     */
    content_type: ContentType;
    /**
     * The content related to meta - this includes an array of objects with properties id &
     * quantity
     */
    contents: any[];
    /**
     * The currency of the user
     */
    currency: string;
    /**
     * List of the teams the user follows (Only applicable when the logged_in property is set to
     * true)
     */
    followed_team_names?: any[];
    /**
     * Items related to an order
     */
    items?: ProductViewedItem[];
    /**
     * Is set to true when the user is logged in while performing the action
     */
    logged_in: boolean;
    /**
     * Name of the relevant content
     */
    name: string;
    /**
     * The id of the platform the user performed the action on.
     */
    platform_id: string;
    /**
     * Price of the product (In euros)
     */
    price?: number;
    /**
     * Id of the product on the page
     */
    product_id: string;
    /**
     * Name of the product on the page
     */
    product_name: string;
    /**
     * The price of the product
     */
    value?: number;
    /**
     * The size of the window for web; and of the screen for app.
     */
    viewport_size?: ViewportSize;
    [property: string]: any;
}

/**
 * Categories related to the item. Only one category is provided for each type. For products
 * and auctions, they will be based on the directly connected categories. For categories,
 * they will be based on the other categories that its auctions are linked to (For example
 * the team category Liverpool will have the related sport category "Football" because its
 * events will have the "Football" category linked to them. Similarly, Liverpool may have
 * "Poppy Appeal" as the related other category since one of its events may be linked to it).
 */
export interface ProductViewedCategories {
    /**
     * The related athlete category
     */
    athlete?: CunningAthlete;
    /**
     * The related country category
     */
    country?: CunningCountry;
    /**
     * The related league category
     */
    league?: CunningLeague;
    /**
     * The related other category
     */
    other?: CunningOther;
    /**
     * The related sport category
     */
    sport?: CunningSport;
    /**
     * The related team category
     */
    team?: CunningTeam;
    [property: string]: any;
}

/**
 * The related athlete category
 */
export interface CunningAthlete {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related country category
 */
export interface CunningCountry {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related league category
 */
export interface CunningLeague {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related other category
 */
export interface CunningOther {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related sport category
 */
export interface CunningSport {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

/**
 * The related team category
 */
export interface CunningTeam {
    /**
     * The id of the category
     */
    id: string;
    /**
     * The name of the category
     */
    name: string;
    [property: string]: any;
}

export interface ProductViewedItem {
    /**
     * The currency of the item
     */
    currency?: string;
    /**
     * Name of the event related to the product
     */
    event_name?: string;
    /**
     * The framing type of the item
     */
    framing_type?: string;
    /**
     * Index in the list
     */
    index: number;
    /**
     * The id of the item
     */
    item_id: string;
    /**
     * The name of the item
     */
    item_name?: string;
    /**
     * The price of the item related to the currency
     */
    price?: number;
    /**
     * The quantity of items
     */
    quantity: number;
    /**
     * The variant of the item
     */
    variant?: string;
    [property: string]: any;
}

/**
 * Triggered when a quick filter on an event screen is selected
 */
export interface QuickFilterSelected {
    /**
     * Type of the category filter
     */
    name: Name;
    [property: string]: any;
}

/**
 * Type of the category filter
 */
export type Name = "country" | "team";

/**
 * Triggered when a user tries to raise a max bid on an item
 */
export interface RaiseMaxBidClicked {
    /**
     * The end date of the related auction event.
     */
    auction_end_date?: Date;
    /**
     * The amount the bid incremented by
     */
    bid_increment: number;
    /**
     * The amount the bid incremented by in cent
     */
    bid_increment_cents?: number;
    /**
     * The currency the user bid in
     */
    currency: string;
    /**
     * Name of the featured team of the event
     */
    event_featured_team_name?: string;
    /**
     * Id of event related to the product that the user bid on
     */
    event_id?: string;
    /**
     * Name of event related to the product that the user bid on
     */
    event_name?: string;
    /**
     * The amount of the max bid
     */
    max_bid_amount: number;
    /**
     * The amount of the max bid in cents
     */
    max_bid_amount_cents: number;
    /**
     * The amount of the max bid the user is trying to raise
     */
    old_max_bid_amount: number;
    /**
     * The amount of the max bid the user is trying to raise in cents
     */
    old_max_bid_amount_cents: number;
    /**
     * Id of partner related to the product that the user bid on
     */
    partner_id: string;
    /**
     * Id of product that the user bid on
     */
    product_id: string;
    /**
     * Name of product that the user bid on
     */
    product_name: string;
    /**
     * The price of the bid (related to the currency)
     */
    product_price: number;
    /**
     * Price of the product in cents
     */
    product_price_cents?: number;
    /**
     * The price of the product in euros
     */
    product_price_euro: number;
    /**
     * Price of the product in cents (euros cents)
     */
    product_price_euro_cents?: number;
    /**
     * Segement spec name for this event (used for automatic mapping to GA4/Meta). Always set to
     * "Product Added"
     */
    segment_spec_name: SegmentSpecName;
    [property: string]: any;
}

/**
 * Triggered when a user selects a recently searched item
 */
export interface RecentSearchItemClicked {
    /**
     * The term the user searched
     */
    search_term: string;
    [property: string]: any;
}

/**
 * Triggered when a user views a registration form
 */
export interface RegistrationFormViewed {
    /**
     * The page slug (Web only)
     */
    slug?: string;
    /**
     * The starting action the user took to open the form
     */
    starting_point?: StartingPoint;
    [property: string]: any;
}

/**
 * Triggered when a user has successfully registers
 */
export interface RegistrationSucceeded {
    /**
     * user_id used for deduplication
     */
    event_id?: string;
    /**
     * The list of the users identity providers
     */
    identity_providers: any[];
    /**
     * What was the user doing before logging in
     */
    onboarding_starting_point: StartingPoint;
    [property: string]: any;
}

/**
 * Triggered when a user selects a search result
 */
export interface SearchItemClicked {
    /**
     * The name of the result
     */
    name?: string;
    /**
     * The term the user entered
     */
    search_term?: string;
    /**
     * The tab on the search page the user selected the result
     */
    tab_origin?: string;
    /**
     * The type of result the user selected the result from
     */
    type: SearchItemClickedType;
    [property: string]: any;
}

/**
 * The type of result the user selected the result from
 */
export type SearchItemClickedType = "Athlete" | "Condition" | "Country" | "Gender" | "Highlight" | "League" | "Other" | "Sport" | "Team" | "athlete" | "condition" | "country" | "event" | "gender" | "highlight" | "league" | "other" | "product" | "seeAll" | "sport" | "team";

/**
 * Triggered when the search navigation button is clicked
 */
export interface SearchModalOpened {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    [property: string]: any;
}

/**
 * Triggered when a user selects a suggested search result
 */
export interface SearchSuggestionClicked {
    /**
     * The suggestion the user selected
     */
    search_suggestion: string;
    /**
     * The term the user entered
     */
    search_term: string;
    [property: string]: any;
}

/**
 * Triggered when a user signs up for an account
 */
export interface SignedUp {
    /**
     * This is used as a deduplication key so it is the same for the backend and frontend. This
     * is set to user id and not the "MatchWornShirt" event id.
     */
    event_id?: string;
    [property: string]: any;
}

/**
 * The user has applied sorting on a list of products
 */
export interface SortingApplied {
    /**
     * The page type where the sorting was applied (e.g. all-products page, search page, event
     * page etc.)
     */
    page_type: string;
    /**
     * Whether the sorting was applied in ascending or descending order
     */
    sorting_option: string;
    /**
     * The property by which the user sorts
     */
    sorting_type: string;
    [property: string]: any;
}

/**
 * Triggered when a menu hierarchy is clicked
 */
export interface TopMenuItemClicked {
    /**
     * The name of the menu hierarchy
     */
    item_name?: string;
    properties: any;
    [property: string]: any;
}

/**
 * Triggered when the upsell screen is viewed after payment
 */
export interface UpsellScreenViewed {
    /**
     * The discount percentage that the user received, if 0 the user will not see any discount
     * information, if it is more than 0 it will be shown on the upsell screen
     */
    discount_percentage: number;
    /**
     * The price of the original item
     */
    item_price: UpsellScreenViewedItemPrice;
    /**
     * The id of the order
     */
    order_id: string;
    [property: string]: any;
}

/**
 * The price of the original item
 */
export interface UpsellScreenViewedItemPrice {
    currency?: string;
    price?:    number;
    [property: string]: any;
}

/**
 * Triggered when the user views a step in the verification onboarding flow.
 */
export interface VerificationOnboardingStepViewed {
    /**
     * The bid amount user tried to place in the users currency
     */
    bid_amount?: number;
    /**
     * The currency of the user
     */
    bid_amount_currency: string;
    /**
     * The name of the step the user is viewing
     */
    step: Step;
    /**
     * The current bid limit of the user
     */
    user_bid_limit: number;
    [property: string]: any;
}

/**
 * The name of the step the user is viewing
 */
export type Step = "Address" | "Completed" | "Email" | "Overview" | "Payment" | "Phone";

/**
 * Triggered on press of an action card, located at the end of a products horizontal slider
 * on an event list item (whenever there are more products available than displayed)
 */
export interface ViewAllProductsClicked {
    /**
     * Name of the event which the products belong to
     */
    event_name?: string;
    [property: string]: any;
}


/**
 * The analytics.js snippet should be available via window.analytics.
 * You can install it by following instructions at: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/
 * Make sure to also include the TypeScript declarations with: `npm install @segment/analytics-next` (install with --save-dev for type definitions only).
 *
 * If you don't want to use the snippet, you can also install the `@segment/analytics-next` library as a *production* dependency and use it like this: 
 * ```ts
 * import { AnalyticsBrowser } from '@segment/analytics-next' 
 * import { setTypewriterOptions } from './analytics'
 *
 * const analytics = AnalyticsBrowser.load({ writeKey: 'SEGMENT_WRITE_KEY' })
 * 
 * setTypewriterOptions({ analytics: analytics })
 */
import type { AnalyticsSnippet, Analytics, AnalyticsBrowser, Options } from '@segment/analytics-next'

declare global {
    interface Window {
        analytics: AnalyticsSnippet;
    }
}

type AnyAnalytics = AnalyticsSnippet | Analytics | AnalyticsBrowser

/** The callback exposed by analytics.js. */
export type Callback = () => void

export type ViolationHandler = (
    message: Record<string, any>,
    violations: any[]
) => void

/**
 * The default handler that is fired if none is supplied with setTypewriterOptions.
 * This handler will log a warning message to the console.
 */
export const defaultValidationErrorHandler: ViolationHandler = (message, violations) => {
    const msg = JSON.stringify(
        {
            type: 'Typewriter JSON Schema Validation Error',
            description:
                `You made an analytics call (${message.event}) using Typewriter that doesn't match the ` +
                'Tracking Plan spec.',
            errors: violations,
        },
        undefined,
        2,
    );

    console.warn(msg);
};


let analytics: () => AnyAnalytics | undefined = () => {
    return window.analytics;
};

/** Options to customize the runtime behavior of a Typewriter client. */
export interface TypewriterOptions {
    /**
     * Underlying analytics instance where analytics calls are forwarded on to.
     * Defaults to window.analytics.
     */
    analytics?: AnyAnalytics;
    /**
     * Handler fired when if an event does not match its spec. This handler
     * does not fire in production mode, because it requires inlining the full
     * JSON Schema spec for each event in your Tracking Plan.
     *
     * By default, it will throw errors if NODE_ENV = "test" so that tests will fail
     * if a message does not match the spec. Otherwise, errors will be logged to stderr.
     */
    onViolation?: ViolationHandler;
}

/**
 * Updates the run-time configuration of this Typewriter client.
 *
 * @param {TypewriterOptions} options - the options to upsert
 *
 * @typedef {Object} TypewriterOptions
 * @property {AnyAnalytics} [analytics] - Underlying analytics instance where analytics
 * 		calls are forwarded on to. Defaults to window.analytics.
 * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
 * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
 * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
 * 		will be logged to stderr.
 */
export function setTypewriterOptions(options: TypewriterOptions) {
    analytics = options.analytics ? () => options.analytics || window.analytics : analytics;
}


/**
 * Helper to attach metadata on Typewriter to outbound requests.
 * This is used for attribution and debugging by the Segment team.
 */
function withTypewriterContext(message: Options = {}): Options {
    return {
        ...message,
        context: {
            ...(message.context || {}),
            typewriter: {
                language: 'typescript',
                version: '9.2.0',
            },
        },
    };
}

/**
 * Fires a 'AccountCreationPopupClicked' track call.
 *
 * @param AccountCreationPopupClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function accountCreationPopupClicked(props: AccountCreationPopupClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Account Creation Popup Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AccountCreationPopupShown' track call.
 *
 * @param AccountCreationPopupShown props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function accountCreationPopupShown(props: AccountCreationPopupShown, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Account Creation Popup Shown', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AccountCreationPopupSkipped' track call.
 *
 * @param AccountCreationPopupSkipped props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function accountCreationPopupSkipped(props: AccountCreationPopupSkipped, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Account Creation Popup Skipped', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AddNewCardClicked' track call.
 *
 * @param AddNewCardClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function addNewCardClicked(props: AddNewCardClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Add New Card Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AnnouncementClicked' track call.
 *
 * @param AnnouncementClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function announcementClicked(props: AnnouncementClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Announcement Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AuthenticatedByOneTap' track call.
 *
 * @param AuthenticatedByOneTap props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function authenticatedByOneTap(props: AuthenticatedByOneTap, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Authenticated By One Tap', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'BeginCheckout' track call.
 *
 * @param BeginCheckout props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function beginCheckout(props: BeginCheckout, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Begin Checkout', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'BidClicked' track call.
 *
 * @param BidClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function bidClicked(props: BidClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Bid Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'BidPlaced' track call.
 *
 * @param BidPlaced props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function bidPlaced(props: BidPlaced, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Bid Placed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'BuyNow' track call.
 *
 * @param BuyNow props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function buyNow(props: BuyNow, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Buy Now', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CallToActionBlockClicked' track call.
 *
 * @param CallToActionBlockClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function callToActionBlockClicked(props: CallToActionBlockClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Call To Action Block Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CardBlockClicked' track call.
 *
 * @param CardBlockClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function cardBlockClicked(props: CardBlockClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Card Block Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CardTokenizationFailed' track call.
 *
 * @param CardTokenizationFailed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function cardTokenizationFailed(props: CardTokenizationFailed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Card Tokenization Failed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CategoryClicked' track call.
 *
 * @param CategoryClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function categoryClicked(props: CategoryClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Category Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CategoryFollowed' track call.
 *
 * @param CategoryFollowed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function categoryFollowed(props: CategoryFollowed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Category Followed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CategoryUnfollowed' track call.
 *
 * @param CategoryUnfollowed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function categoryUnfollowed(props: CategoryUnfollowed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Category Unfollowed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CategoryViewed' track call.
 *
 * @param CategoryViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function categoryViewed(props: CategoryViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Category Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CheckoutStarted' track call.
 *
 * @param CheckoutStarted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function checkoutStarted(props: CheckoutStarted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Checkout Started', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ClickedRecentlyViewedProduct' track call.
 *
 * @param ClickedRecentlyViewedProduct props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function clickedRecentlyViewedProduct(props: ClickedRecentlyViewedProduct, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Clicked Recently Viewed Product', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ConfirmBidClicked' track call.
 *
 * @param ConfirmBidClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function confirmBidClicked(props: ConfirmBidClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Confirm Bid Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'DeleteCardClicked' track call.
 *
 * @param DeleteCardClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function deleteCardClicked(props: DeleteCardClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Delete Card Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'EmailVerified' track call.
 *
 * @param EmailVerified props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function emailVerified(props: EmailVerified, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Email Verified', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'EventClicked' track call.
 *
 * @param EventClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function eventClicked(props: EventClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Event Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'EventFollowed' track call.
 *
 * @param EventFollowed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function eventFollowed(props: EventFollowed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Event Followed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'EventNameClicked' track call.
 *
 * @param EventNameClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function eventNameClicked(props: EventNameClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Event Name Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'EventUnfollowed' track call.
 *
 * @param EventUnfollowed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function eventUnfollowed(props: EventUnfollowed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Event Unfollowed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'EventViewed' track call.
 *
 * @param EventViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function eventViewed(props: EventViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Event Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExperimentViewed' track call.
 *
 * @param ExperimentViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function experimentViewed(props: ExperimentViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Experiment Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FabricksAuthenticationPageViewed' track call.
 *
 * @param FabricksAuthenticationPageViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function fabricksAuthenticationPageViewed(props: FabricksAuthenticationPageViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Fabricks Authentication Page Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FavouritesOnboardingScreenCompleted' track call.
 *
 * @param FavouritesOnboardingScreenCompleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function favouritesOnboardingScreenCompleted(props: FavouritesOnboardingScreenCompleted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Favourites Onboarding Screen Completed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FavouritesOnboardingSkipped' track call.
 *
 * @param FavouritesOnboardingSkipped props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function favouritesOnboardingSkipped(props: FavouritesOnboardingSkipped, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Favourites Onboarding Skipped', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FavouritesOnboardingStarted' track call.
 *
 * @param FavouritesOnboardingStarted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function favouritesOnboardingStarted(props: FavouritesOnboardingStarted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Favourites Onboarding Started', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FilterButtonClicked' track call.
 *
 * @param FilterButtonClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function filterButtonClicked(props: FilterButtonClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Filter Button Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FiltersApplied' track call.
 *
 * @param FiltersApplied props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function filtersApplied(props: FiltersApplied, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Filters Applied', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FiltersCleared' track call.
 *
 * @param FiltersCleared props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function filtersCleared(props: FiltersCleared, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Filters Cleared', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FramingPresetSelected' track call.
 *
 * @param FramingPresetSelected props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function framingPresetSelected(props: FramingPresetSelected, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Framing Preset Selected', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FramingSessionStarted' track call.
 *
 * @param FramingSessionStarted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function framingSessionStarted(props: FramingSessionStarted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Framing Session Started', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FramingUpsellClicked' track call.
 *
 * @param FramingUpsellClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function framingUpsellClicked(props: FramingUpsellClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Framing Upsell Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FullWidthContentClicked' track call.
 *
 * @param FullWidthContentClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function fullWidthContentClicked(props: FullWidthContentClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Full Width Content Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'GiveawayJoinButtonClicked' track call.
 *
 * @param GiveawayJoinButtonClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function giveawayJoinButtonClicked(props: GiveawayJoinButtonClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Giveaway Join Button Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'HighlightedMenuLinkClicked' track call.
 *
 * @param HighlightedMenuLinkClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function highlightedMenuLinkClicked(props: HighlightedMenuLinkClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Highlighted Menu Link Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'LeaguePressed' track call.
 *
 * @param LeaguePressed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function leaguePressed(props: LeaguePressed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('League Pressed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'LoginClicked' track call.
 *
 * @param LoginClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function loginClicked(props: LoginClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Login Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'LoginFormViewed' track call.
 *
 * @param LoginFormViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function loginFormViewed(props: LoginFormViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Login Form Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'LoginSucceeded' track call.
 *
 * @param LoginSucceeded props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function loginSucceeded(props: LoginSucceeded, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Login Succeeded', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'LogoPressed' track call.
 *
 * @param LogoPressed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function logoPressed(props: LogoPressed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Logo Pressed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'MaxBidClicked' track call.
 *
 * @param MaxBidClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function maxBidClicked(props: MaxBidClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Max Bid Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'MaxBidPlaced' track call.
 *
 * @param MaxBidPlaced props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function maxBidPlaced(props: MaxBidPlaced, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Max Bid Placed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'MenuItemClicked' track call.
 *
 * @param MenuItemClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function menuItemClicked(props: MenuItemClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Menu Item Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'NewsletterFormSubmitted' track call.
 *
 * @param NewsletterFormSubmitted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function newsletterFormSubmitted(props: NewsletterFormSubmitted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Newsletter Form Submitted', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'NotificationClicked' track call.
 *
 * @param NotificationClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function notificationClicked(props: NotificationClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Notification Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'NotificationsMarkedAllAsRead' track call.
 *
 * @param NotificationsMarkedAllAsRead props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function notificationsMarkedAllAsRead(props: NotificationsMarkedAllAsRead, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Notifications Marked All As Read', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'NotificationsViewed' track call.
 *
 * @param NotificationsViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function notificationsViewed(props: NotificationsViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Notifications Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'NowLiveItemClicked' track call.
 *
 * @param NowLiveItemClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function nowLiveItemClicked(props: NowLiveItemClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Now Live Item Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'NowLiveViewed' track call.
 *
 * @param NowLiveViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function nowLiveViewed(props: NowLiveViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Now Live Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'OrderCompleted' track call.
 *
 * @param OrderCompleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function orderCompleted(props: OrderCompleted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Order Completed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'OrderPaid' track call.
 *
 * @param OrderPaid props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function orderPaid(props: OrderPaid, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Order Paid', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PageViewed' track call.
 *
 * @param PageViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function pageViewed(props: PageViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Page Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PayButtonClicked' track call.
 *
 * @param PayButtonClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function payButtonClicked(props: PayButtonClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Pay Button Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PreAuthCreditCardSelected' track call.
 *
 * @param PreAuthCreditCardSelected props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function preAuthCreditCardSelected(props: PreAuthCreditCardSelected, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Pre Auth Credit Card Selected', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PreferencesOnboardingFlowExited' track call.
 *
 * @param PreferencesOnboardingFlowExited props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function preferencesOnboardingFlowExited(props: PreferencesOnboardingFlowExited, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Preferences Onboarding Flow Exited', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PreferencesOnboardingModalOpened' track call.
 *
 * @param PreferencesOnboardingModalOpened props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function preferencesOnboardingModalOpened(props: PreferencesOnboardingModalOpened, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Preferences Onboarding Modal Opened', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductCarouselOpened' track call.
 *
 * @param ProductCarouselOpened props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productCarouselOpened(props: ProductCarouselOpened, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Carousel Opened', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductClicked' track call.
 *
 * @param ProductClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productClicked(props: ProductClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductFollowed' track call.
 *
 * @param ProductFollowed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productFollowed(props: ProductFollowed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Followed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductImagesCarrousel' track call.
 *
 * @param ProductImagesCarrousel props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productImagesCarrousel(props: ProductImagesCarrousel, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Images Carrousel', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductTabViewed' track call.
 *
 * @param ProductTabViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productTabViewed(props: ProductTabViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Tab Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductUnfollowed' track call.
 *
 * @param ProductUnfollowed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productUnfollowed(props: ProductUnfollowed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Unfollowed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductViewed' track call.
 *
 * @param ProductViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productViewed(props: ProductViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'QuickFilterSelected' track call.
 *
 * @param QuickFilterSelected props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function quickFilterSelected(props: QuickFilterSelected, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Quick Filter Selected', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'RaiseMaxBidClicked' track call.
 *
 * @param RaiseMaxBidClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function raiseMaxBidClicked(props: RaiseMaxBidClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Raise Max Bid Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'RecentSearchItemClicked' track call.
 *
 * @param RecentSearchItemClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function recentSearchItemClicked(props: RecentSearchItemClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Recent Search Item Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'RegistrationFormViewed' track call.
 *
 * @param RegistrationFormViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function registrationFormViewed(props: RegistrationFormViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Registration Form Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'RegistrationSucceeded' track call.
 *
 * @param RegistrationSucceeded props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function registrationSucceeded(props: RegistrationSucceeded, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Registration Succeeded', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'SearchItemClicked' track call.
 *
 * @param SearchItemClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function searchItemClicked(props: SearchItemClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Search Item Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'SearchModalOpened' track call.
 *
 * @param SearchModalOpened props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function searchModalOpened(props: SearchModalOpened, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Search Modal Opened', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'SearchSuggestionClicked' track call.
 *
 * @param SearchSuggestionClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function searchSuggestionClicked(props: SearchSuggestionClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Search Suggestion Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'SignedUp' track call.
 *
 * @param SignedUp props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function signedUp(props: SignedUp, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Signed Up', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'SortingApplied' track call.
 *
 * @param SortingApplied props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function sortingApplied(props: SortingApplied, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Sorting Applied', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'TopMenuItemClicked' track call.
 *
 * @param TopMenuItemClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function topMenuItemClicked(props: TopMenuItemClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Top Menu Item Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'UpsellScreenViewed' track call.
 *
 * @param UpsellScreenViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function upsellScreenViewed(props: UpsellScreenViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Upsell Screen Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'VerificationOnboardingStepViewed' track call.
 *
 * @param VerificationOnboardingStepViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function verificationOnboardingStepViewed(props: VerificationOnboardingStepViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Verification Onboarding Step Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ViewAllProductsClicked' track call.
 *
 * @param ViewAllProductsClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function viewAllProductsClicked(props: ViewAllProductsClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('View All Products Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}

const clientAPI = {
    /**
     * Updates the run-time configuration of this Typewriter client.
     *
     * @param {TypewriterOptions} options - the options to upsert
     *
     * @typedef {Object} TypewriterOptions
     * @property {AnyAnalytics} [analytics] - Underlying analytics instance where analytics
     * 		calls are forwarded on to. Defaults to window.analytics.
     * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
     * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
     * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
     * 		will be logged to stderr.
     */
    setTypewriterOptions,

    /**
     * Fires a 'AccountCreationPopupClicked' track call.
     *
     * @param AccountCreationPopupClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    accountCreationPopupClicked,
    /**
     * Fires a 'AccountCreationPopupShown' track call.
     *
     * @param AccountCreationPopupShown props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    accountCreationPopupShown,
    /**
     * Fires a 'AccountCreationPopupSkipped' track call.
     *
     * @param AccountCreationPopupSkipped props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    accountCreationPopupSkipped,
    /**
     * Fires a 'AddNewCardClicked' track call.
     *
     * @param AddNewCardClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    addNewCardClicked,
    /**
     * Fires a 'AnnouncementClicked' track call.
     *
     * @param AnnouncementClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    announcementClicked,
    /**
     * Fires a 'AuthenticatedByOneTap' track call.
     *
     * @param AuthenticatedByOneTap props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    authenticatedByOneTap,
    /**
     * Fires a 'BeginCheckout' track call.
     *
     * @param BeginCheckout props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    beginCheckout,
    /**
     * Fires a 'BidClicked' track call.
     *
     * @param BidClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    bidClicked,
    /**
     * Fires a 'BidPlaced' track call.
     *
     * @param BidPlaced props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    bidPlaced,
    /**
     * Fires a 'BuyNow' track call.
     *
     * @param BuyNow props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    buyNow,
    /**
     * Fires a 'CallToActionBlockClicked' track call.
     *
     * @param CallToActionBlockClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    callToActionBlockClicked,
    /**
     * Fires a 'CardBlockClicked' track call.
     *
     * @param CardBlockClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    cardBlockClicked,
    /**
     * Fires a 'CardTokenizationFailed' track call.
     *
     * @param CardTokenizationFailed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    cardTokenizationFailed,
    /**
     * Fires a 'CategoryClicked' track call.
     *
     * @param CategoryClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    categoryClicked,
    /**
     * Fires a 'CategoryFollowed' track call.
     *
     * @param CategoryFollowed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    categoryFollowed,
    /**
     * Fires a 'CategoryUnfollowed' track call.
     *
     * @param CategoryUnfollowed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    categoryUnfollowed,
    /**
     * Fires a 'CategoryViewed' track call.
     *
     * @param CategoryViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    categoryViewed,
    /**
     * Fires a 'CheckoutStarted' track call.
     *
     * @param CheckoutStarted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    checkoutStarted,
    /**
     * Fires a 'ClickedRecentlyViewedProduct' track call.
     *
     * @param ClickedRecentlyViewedProduct props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    clickedRecentlyViewedProduct,
    /**
     * Fires a 'ConfirmBidClicked' track call.
     *
     * @param ConfirmBidClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    confirmBidClicked,
    /**
     * Fires a 'DeleteCardClicked' track call.
     *
     * @param DeleteCardClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    deleteCardClicked,
    /**
     * Fires a 'EmailVerified' track call.
     *
     * @param EmailVerified props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    emailVerified,
    /**
     * Fires a 'EventClicked' track call.
     *
     * @param EventClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    eventClicked,
    /**
     * Fires a 'EventFollowed' track call.
     *
     * @param EventFollowed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    eventFollowed,
    /**
     * Fires a 'EventNameClicked' track call.
     *
     * @param EventNameClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    eventNameClicked,
    /**
     * Fires a 'EventUnfollowed' track call.
     *
     * @param EventUnfollowed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    eventUnfollowed,
    /**
     * Fires a 'EventViewed' track call.
     *
     * @param EventViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    eventViewed,
    /**
     * Fires a 'ExperimentViewed' track call.
     *
     * @param ExperimentViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    experimentViewed,
    /**
     * Fires a 'FabricksAuthenticationPageViewed' track call.
     *
     * @param FabricksAuthenticationPageViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    fabricksAuthenticationPageViewed,
    /**
     * Fires a 'FavouritesOnboardingScreenCompleted' track call.
     *
     * @param FavouritesOnboardingScreenCompleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    favouritesOnboardingScreenCompleted,
    /**
     * Fires a 'FavouritesOnboardingSkipped' track call.
     *
     * @param FavouritesOnboardingSkipped props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    favouritesOnboardingSkipped,
    /**
     * Fires a 'FavouritesOnboardingStarted' track call.
     *
     * @param FavouritesOnboardingStarted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    favouritesOnboardingStarted,
    /**
     * Fires a 'FilterButtonClicked' track call.
     *
     * @param FilterButtonClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    filterButtonClicked,
    /**
     * Fires a 'FiltersApplied' track call.
     *
     * @param FiltersApplied props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    filtersApplied,
    /**
     * Fires a 'FiltersCleared' track call.
     *
     * @param FiltersCleared props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    filtersCleared,
    /**
     * Fires a 'FramingPresetSelected' track call.
     *
     * @param FramingPresetSelected props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    framingPresetSelected,
    /**
     * Fires a 'FramingSessionStarted' track call.
     *
     * @param FramingSessionStarted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    framingSessionStarted,
    /**
     * Fires a 'FramingUpsellClicked' track call.
     *
     * @param FramingUpsellClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    framingUpsellClicked,
    /**
     * Fires a 'FullWidthContentClicked' track call.
     *
     * @param FullWidthContentClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    fullWidthContentClicked,
    /**
     * Fires a 'GiveawayJoinButtonClicked' track call.
     *
     * @param GiveawayJoinButtonClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    giveawayJoinButtonClicked,
    /**
     * Fires a 'HighlightedMenuLinkClicked' track call.
     *
     * @param HighlightedMenuLinkClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    highlightedMenuLinkClicked,
    /**
     * Fires a 'LeaguePressed' track call.
     *
     * @param LeaguePressed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    leaguePressed,
    /**
     * Fires a 'LoginClicked' track call.
     *
     * @param LoginClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    loginClicked,
    /**
     * Fires a 'LoginFormViewed' track call.
     *
     * @param LoginFormViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    loginFormViewed,
    /**
     * Fires a 'LoginSucceeded' track call.
     *
     * @param LoginSucceeded props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    loginSucceeded,
    /**
     * Fires a 'LogoPressed' track call.
     *
     * @param LogoPressed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    logoPressed,
    /**
     * Fires a 'MaxBidClicked' track call.
     *
     * @param MaxBidClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    maxBidClicked,
    /**
     * Fires a 'MaxBidPlaced' track call.
     *
     * @param MaxBidPlaced props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    maxBidPlaced,
    /**
     * Fires a 'MenuItemClicked' track call.
     *
     * @param MenuItemClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    menuItemClicked,
    /**
     * Fires a 'NewsletterFormSubmitted' track call.
     *
     * @param NewsletterFormSubmitted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    newsletterFormSubmitted,
    /**
     * Fires a 'NotificationClicked' track call.
     *
     * @param NotificationClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    notificationClicked,
    /**
     * Fires a 'NotificationsMarkedAllAsRead' track call.
     *
     * @param NotificationsMarkedAllAsRead props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    notificationsMarkedAllAsRead,
    /**
     * Fires a 'NotificationsViewed' track call.
     *
     * @param NotificationsViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    notificationsViewed,
    /**
     * Fires a 'NowLiveItemClicked' track call.
     *
     * @param NowLiveItemClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    nowLiveItemClicked,
    /**
     * Fires a 'NowLiveViewed' track call.
     *
     * @param NowLiveViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    nowLiveViewed,
    /**
     * Fires a 'OrderCompleted' track call.
     *
     * @param OrderCompleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    orderCompleted,
    /**
     * Fires a 'OrderPaid' track call.
     *
     * @param OrderPaid props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    orderPaid,
    /**
     * Fires a 'PageViewed' track call.
     *
     * @param PageViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    pageViewed,
    /**
     * Fires a 'PayButtonClicked' track call.
     *
     * @param PayButtonClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    payButtonClicked,
    /**
     * Fires a 'PreAuthCreditCardSelected' track call.
     *
     * @param PreAuthCreditCardSelected props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    preAuthCreditCardSelected,
    /**
     * Fires a 'PreferencesOnboardingFlowExited' track call.
     *
     * @param PreferencesOnboardingFlowExited props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    preferencesOnboardingFlowExited,
    /**
     * Fires a 'PreferencesOnboardingModalOpened' track call.
     *
     * @param PreferencesOnboardingModalOpened props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    preferencesOnboardingModalOpened,
    /**
     * Fires a 'ProductCarouselOpened' track call.
     *
     * @param ProductCarouselOpened props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productCarouselOpened,
    /**
     * Fires a 'ProductClicked' track call.
     *
     * @param ProductClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productClicked,
    /**
     * Fires a 'ProductFollowed' track call.
     *
     * @param ProductFollowed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productFollowed,
    /**
     * Fires a 'ProductImagesCarrousel' track call.
     *
     * @param ProductImagesCarrousel props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productImagesCarrousel,
    /**
     * Fires a 'ProductTabViewed' track call.
     *
     * @param ProductTabViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productTabViewed,
    /**
     * Fires a 'ProductUnfollowed' track call.
     *
     * @param ProductUnfollowed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productUnfollowed,
    /**
     * Fires a 'ProductViewed' track call.
     *
     * @param ProductViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productViewed,
    /**
     * Fires a 'QuickFilterSelected' track call.
     *
     * @param QuickFilterSelected props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    quickFilterSelected,
    /**
     * Fires a 'RaiseMaxBidClicked' track call.
     *
     * @param RaiseMaxBidClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    raiseMaxBidClicked,
    /**
     * Fires a 'RecentSearchItemClicked' track call.
     *
     * @param RecentSearchItemClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    recentSearchItemClicked,
    /**
     * Fires a 'RegistrationFormViewed' track call.
     *
     * @param RegistrationFormViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    registrationFormViewed,
    /**
     * Fires a 'RegistrationSucceeded' track call.
     *
     * @param RegistrationSucceeded props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    registrationSucceeded,
    /**
     * Fires a 'SearchItemClicked' track call.
     *
     * @param SearchItemClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    searchItemClicked,
    /**
     * Fires a 'SearchModalOpened' track call.
     *
     * @param SearchModalOpened props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    searchModalOpened,
    /**
     * Fires a 'SearchSuggestionClicked' track call.
     *
     * @param SearchSuggestionClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    searchSuggestionClicked,
    /**
     * Fires a 'SignedUp' track call.
     *
     * @param SignedUp props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    signedUp,
    /**
     * Fires a 'SortingApplied' track call.
     *
     * @param SortingApplied props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    sortingApplied,
    /**
     * Fires a 'TopMenuItemClicked' track call.
     *
     * @param TopMenuItemClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    topMenuItemClicked,
    /**
     * Fires a 'UpsellScreenViewed' track call.
     *
     * @param UpsellScreenViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    upsellScreenViewed,
    /**
     * Fires a 'VerificationOnboardingStepViewed' track call.
     *
     * @param VerificationOnboardingStepViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    verificationOnboardingStepViewed,
    /**
     * Fires a 'ViewAllProductsClicked' track call.
     *
     * @param ViewAllProductsClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    viewAllProductsClicked,
};

export default new Proxy<typeof clientAPI>(clientAPI, {
    get(target, method) {
        if (typeof method === 'string' && target.hasOwnProperty(method)) {
            return target[method as keyof typeof clientAPI];
        }

        return () => {
            console.warn(`⚠️  You made an analytics call (${String(method)}) that can't be found. Either:
         a) Re-generate your typewriter client: \`npx typewriter\`
         b) Add it to your Tracking Plan: https://app.segment.com/segment-oscb/protocols/tracking-plans/rs_1zTHJU9fd5mt7cndWnd4PgJbMCE`);
            const a = analytics();
            if (a) {
                a.track(
                    'Unknown Analytics Call Fired',
                    {
                        method,
                    },
                    withTypewriterContext(),
                );
            }
        };
    },
});

